import type { ReactNode } from 'react';

/**
 * The heading strip every Finance page opens with.
 *
 * Settlements and Wallet had each grown their own: a grey card, a black
 * title, a paragraph, buttons on the right. Nothing wrong with any one of
 * them, but together the section read as a wall of the same grey — the
 * reviewer's word was "all black" — with no cue where one page's concern
 * ended and the next began, and nothing of the brand in sight.
 *
 * One component, one look: a gold rule down the left as the section's mark,
 * the title, a one-line purpose, and the actions. Pages pass their actions in;
 * they do not restyle the header.
 */
export function FinancePageHeader({
  title,
  purpose,
  actions,
}: {
  title: string;
  purpose: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
      <div className="border-gold-500 border-l-[3px] pl-3.5">
        <h3 className="text-ink text-[15px] font-bold tracking-tight">{title}</h3>
        <p className="text-ink-subtle mt-0.5 text-[12.5px] font-medium">{purpose}</p>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>}
    </div>
  );
}

/** A panel heading inside a Finance page: gold mark, title, optional right slot. */
export function FinancePanelHeader({
  title,
  description,
  right,
}: {
  title: string;
  description?: string;
  right?: ReactNode;
}) {
  return (
    <div className="border-border/60 flex flex-wrap items-center justify-between gap-2 border-b px-5 py-3.5">
      <div className="flex items-start gap-2.5">
        <span className="bg-gold-500 mt-[5px] h-3.5 w-1 shrink-0 rounded-full" aria-hidden="true" />
        <div>
          <h4 className="text-ink text-[14px] font-bold">{title}</h4>
          {description && (
            <p className="text-ink-subtle mt-0.5 text-[12px] font-medium">{description}</p>
          )}
        </div>
      </div>
      {right}
    </div>
  );
}
